import type { SearchClient } from 'algoliasearch';
import type { GetServerSidePropsContext } from 'next';
import { renderToString } from 'react-dom/server';
import {
  Configure,
  getServerState,
  InstantSearch,
  InstantSearchSSRProvider,
  useHits,
} from 'react-instantsearch';

import { getLocaleFromParams } from '@/utils/locale';

import {
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StoreHit } from '@/integrations/algolia/types';
import { fetchCMSDefaultPageBoutiqueListCollectionData } from '@/integrations/contentful';
import { getCountryNameFromCode } from '@/lib/boutique/helpers';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { getTranslatedCountryName } from '@/lib/i18n';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
  isValidCountryCode,
} from '@/lib/regions';
import { CacheHeaderType } from '@/lib/revalidate';
import { BoutiqueListView, HITSPERPAGE } from '@/views/BoutiqueListView';

type MyGetStaticPropsContext = GetServerSidePropsContext<{
  country?: string;
  lang?: string;
}>;

function DummyBoutiqueListPage({
  facetFilters,
  indexName,
  query,
  searchClient,
}: {
  facetFilters: (string | string[])[];
  indexName: string;
  query?: string;
  searchClient: SearchClient;
}) {
  return (
    <InstantSearchSSRProvider>
      <InstantSearch
        indexName={indexName}
        insights={false}
        searchClient={searchClient}
      >
        <Configure
          facetFilters={facetFilters}
          hitsPerPage={HITSPERPAGE}
          query={query}
        />
        <DummyHitsComponent />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}

function DummyHitsComponent() {
  const { hits } = useHits<StoreHit>();
  return <>{hits?.map((hit) => hit.store_id)}</>;
}

export const getServerSideProps = commonServerSideProps(
  async (context: MyGetStaticPropsContext) => {
    const draftMode = resolveContentDraftMode(context);
    const countryCodeSlug = context.params?.country?.toUpperCase();

    if (!countryCodeSlug || !isValidCountryCode(countryCodeSlug)) {
      return {
        notFound: true,
      };
    }

    const countryName = getCountryNameFromCode(countryCodeSlug);
    const locale = getLocaleFromParams(context?.params);
    const region = getRegionFromLocale(locale);
    const saleorLanguageCode = region.locale.locale;
    const contentfulLocale = region.locale.contentful ?? region.locale.locale;
    const { countryCode } = getContentfulRegionFromLocale(locale);
    const isPreview = draftMode;
    const indexName = getIndexName(
      'STORE',
      locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
    );

    const facetFilters = [
      [`type:BOUTIQUE`, `type:FLAG_SHIP_BOUTIQUE`],
      `country_code:${context.query?.country}`,
      `web_visible:true`,
    ];

    const host = context.req.headers.host;
    const protocol = context.req.headers['x-forwarded-proto'] || 'https';
    const url = new URL(context.resolvedUrl, `${protocol}://${host}`);

    if (url.pathname?.includes('/stores/breitling-boutiques/')) {
      const pageVal = url.searchParams.get('page');
      if (pageVal) {
        const cleanedPageVal = pageVal.replace(/\/+/g, '');
        if (cleanedPageVal !== pageVal) {
          url.searchParams.set('page', cleanedPageVal);
          return {
            redirect: {
              destination: url.toString(),
              permanent: false,
            },
          };
        }
      }
    }

    const initialUiState = buildUiStateFromURL(context.resolvedUrl, indexName);

    const countryNameTranslated = getTranslatedCountryName(
      saleorLanguageCode,
      countryCodeSlug,
      countryName,
    );
    let cmsPageBoutiqueListCollectionData =
      await fetchCMSDefaultPageBoutiqueListCollectionData(
        countryName,
        undefined,
        isPreview,
        countryCode,
        contentfulLocale,
      );
    const pageBoutiqueListCollection =
      cmsPageBoutiqueListCollectionData?.PageBoutiqueListCollectionData?.data
        ?.pageBoutiqueListCollection;
    if (pageBoutiqueListCollection && pageBoutiqueListCollection.total === 0) {
      cmsPageBoutiqueListCollectionData =
        await fetchCMSDefaultPageBoutiqueListCollectionData(
          undefined,
          true,
          isPreview,
          countryCode,
          contentfulLocale,
        );
    }

    const cmsDataAboveBoutiqueList =
      cmsPageBoutiqueListCollectionData?.PageBoutiqueListCollectionData.data
        .pageBoutiqueListCollection?.items[0]?.contentAboveBoutiquesCollection
        ?.items || [];
    const cmsDataBelowBoutiqueList =
      cmsPageBoutiqueListCollectionData?.PageBoutiqueListCollectionData.data
        .pageBoutiqueListCollection?.items[0]?.contentBelowBoutiquesCollection
        ?.items || [];

    const algoliaSsrSearchClient = createSearchClient({
      isPreview: draftMode,
      isSsr: true,
    });
    const index = algoliaSsrSearchClient.initIndex(indexName);

    // Perform a search request to get nbPages
    const searchResponse = await index.search('', {
      facetFilters: facetFilters,
      hitsPerPage: HITSPERPAGE,
    });

    if (
      context.query?.page &&
      parseInt(context.query.page as string) > searchResponse.nbPages
    ) {
      return {
        notFound: true,
      };
    }

    const serverState = await getServerState(
      <DummyBoutiqueListPage
        facetFilters={facetFilters}
        indexName={indexName}
        searchClient={algoliaSsrSearchClient}
      />,
      {
        renderToString,
      },
    );

    const newSeoVariables = {
      countryName: countryNameTranslated,
    };
    return {
      props: {
        facetFilters,
        cmsDataAboveBoutiqueList: cmsDataAboveBoutiqueList,
        cmsDataBelowBoutiqueList: cmsDataBelowBoutiqueList,
        seoConfig: cmsPageBoutiqueListCollectionData?.seoConfigBuilder
          .mergeSeoVariables(newSeoVariables)
          ?.build(),
        instantSearchProps: {
          initialUiState,
          indexName,
          serverState,
        },
        serverState,
      },
    };
  },
  CacheHeaderType.NO_CACHE,
);
export default BoutiqueListView;
